/**
 * Brand emblem — a zodiac wheel recreated as a self-contained, resolution-
 * independent SVG (no raster asset): an orange disc ringed by the 12 zodiac
 * glyphs in white circles, a dotted outer ring, and a sun / crescent-moon /
 * third-eye motif at the centre. Size + any backdrop come from `className`.
 */

const SIGNS = ['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓']

// 16 ticks on the outer ring; the four cardinal ones are the accent red.
const DOTS = Array.from({ length: 16 }, (_, i) => ({
  angle: i * 22.5,
  fill: i % 4 === 0 ? '#D33409' : '#F4A87A',
}))

// Fine sunburst behind the centre motif.
const RAYS = Array.from({ length: 72 }, (_, i) => i * 5)

export default function ZodiacLogo({ className = 'w-9 h-9 sm:w-11 sm:h-11 shrink-0' }) {
  return (
    <svg viewBox="0 0 200 200" className={className} role="img" aria-label="Astrologer Raju Maharaj emblem" xmlns="http://www.w3.org/2000/svg">
      {/* Outer ring + degree dots */}
      <circle cx="100" cy="100" r="95" fill="none" stroke="#F4A87A" strokeWidth="1.5" />
      {DOTS.map(({ angle, fill }) => (
        <g key={angle} transform={`translate(100 100) rotate(${angle})`}>
          <circle cx="0" cy="-95" r="3.4" fill={fill} />
        </g>
      ))}

      {/* Main orange disc */}
      <circle cx="100" cy="100" r="84" fill="#F0822E" />

      {/* Centre motif: sunburst → shield → sun / moon / third-eye */}
      <g stroke="#FFFFFF" strokeLinecap="round">
        <g strokeWidth="0.8" opacity="0.8">
          {RAYS.map((a) => (
            <line key={a} transform={`translate(100 100) rotate(${a})`} x1="0" y1="-15" x2="0" y2="-41" />
          ))}
        </g>

        {/* Shield / kite echoing the source emblem */}
        <path d="M79 74 L121 74 L100 141 Z" fill="#F0822E" strokeWidth="1.1" opacity="0.55" />

        {/* Sun (left) */}
        <g strokeWidth="1.2">
          <circle cx="85" cy="80" r="4.6" fill="#FFFFFF" stroke="none" />
          {Array.from({ length: 8 }, (_, i) => i * 45).map((a) => (
            <line key={a} transform={`translate(85 80) rotate(${a})`} x1="0" y1="-6.6" x2="0" y2="-9.2" />
          ))}
        </g>

        {/* Crescent moon (right) */}
        <path d="M118.5 73.5 A7 7 0 1 0 118.5 87.5 A5.4 5.4 0 0 1 118.5 73.5 Z" fill="#FFFFFF" stroke="none" />

        {/* Third eye + mandala ring (bottom-centre) */}
        <circle cx="100" cy="120" r="12" fill="none" strokeWidth="0.8" strokeDasharray="1.6 2.4" opacity="0.75" />
        <path d="M89 120 Q100 112 111 120 Q100 128 89 120 Z" fill="none" strokeWidth="1.5" />
        <circle cx="100" cy="120" r="3.3" fill="#FFFFFF" stroke="none" />
      </g>

      {/* 12 zodiac glyphs, each in a white ring near the inner edge */}
      <g fontFamily="'Segoe UI Symbol', 'Noto Sans Symbols', serif" fontSize="15" textAnchor="middle" fill="#FFFFFF">
        {SIGNS.map((glyph, i) => {
          const a = i * 30
          return (
            <g key={glyph} transform={`translate(100 100) rotate(${a})`}>
              <circle cx="0" cy="-63" r="13" fill="none" stroke="#FFFFFF" strokeWidth="2.4" />
              <text x="0" y="-63" dy="0.36em" transform={`rotate(${-a} 0 -63)`}>{glyph}</text>
            </g>
          )
        })}
      </g>
    </svg>
  )
}
